/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { AppConfig } from 'src/app/app.config';
import { BillOfQuantitiesPosition } from 'src/app/_model/bill-of-quantities-positions';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { DbPersistenceService, Query } from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { BillOfQuantitiesPositionsDaoService } from '../bill-of-quantities-positions-dao.service';
import { BillOfQuantitiesDbTableDefinition } from './bill-of-quantities-positions-db-table-definition';

@Injectable()
export class BillOfQuantitiesDbDaoService implements BillOfQuantitiesPositionsDaoService {
  constructor(
    private logger: LoggingService,
    private dbPersistenceService: DbPersistenceService,
    private billOfQuantitiesTableDefinition: BillOfQuantitiesDbTableDefinition,
  ) { }

  public async saveBillOfQuantitiesPositions(
    billOfQuantitiesPositions: BillOfQuantitiesPosition[]
  ): Promise<void>{
    try {
      await this.clear();
      let db = await this.dbPersistenceService.getImsDb();
      var boqpQueries: Query[] = [];
      for (const boqp of billOfQuantitiesPositions) {
        let boqpQuerie: Query = {
          statement:
            'INSERT OR REPLACE INTO ' +
            this.billOfQuantitiesTableDefinition.tableName +
            '(id, label, databag, hg, og, lg, ulg, boqposnr, baskey, ' +
            ' shortname, amount, unit, boqid, boqlabel)' +
            ' VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?)',
          parameters: [
            Number(boqp.getImsId()),
            boqp.label,
            boqp.databag ? JSON.stringify(boqp.databag): undefined,
            boqp.hg,
            boqp.og,
            boqp.lg,
            boqp.ulg,
            boqp.boqPosNr,
            boqp.basKey,
            boqp.shortName,
            boqp.amount,
            boqp.unit,
            boqp.billOfQuantity
              ? Number(boqp.billOfQuantity.getImsId())
              : undefined,
            boqp.billOfQuantity ? boqp.billOfQuantity.label : undefined,
          ],
        };
        boqpQueries.push(boqpQuerie);
      }
      await this.dbPersistenceService.executeBatch(db, boqpQueries);
    } catch (error) {
      this.logger.log(
        'Error saving bill of quantities to db',
        error,
        '-',
        error?.message
      );
    }
  }

  public async getBillOfQuantitiesPositions(): Promise<BillOfQuantitiesPosition[]> {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let boqp: BillOfQuantitiesPosition[] = [];

      let data = await this.dbPersistenceService.executeQuery(db, {
        statement: 'SELECT * FROM ' + this.billOfQuantitiesTableDefinition.tableName + ';',
        parameters: null,
      });
      data.values
      for (let i = 0; i < data.values.length; i++) {
        let row = data.values[i];
        boqp.push(
          BillOfQuantitiesPosition.fromDto({
            id: row['id'],
            secondaryid: undefined,
            label: row['label'],
            hg: row['hg'],
            og: row['og'],
            lg: row['lg'],
            ulg: row['ulg'],
            boqposnr: row['boqposnr'],
            baskey: row['baskey'],
            shortname: row['shortname'],
            amount: row['amount'],
            unit: row['unit'],
            billofquantities: {
              id: row['boqid'],
              label: row['boqlabel']
            }
          }, JSON.parse(row['databag']))
        );
      }
      return boqp;
    } catch (error) {
      this.logger.log('Error reading bill of quantities positions from db', error);
    }
    return [];
  }

  public async reset() {}

  public async clear() {
    let db = await this.dbPersistenceService.getImsDb();
    this.logger.log(this.constructor.name, "truncate table", this.billOfQuantitiesTableDefinition.tableName)
    await this.dbPersistenceService.truncateTable(db, this.billOfQuantitiesTableDefinition.tableName);
  }

}
